<script setup lang="ts">
import { IconsCategoryBil, IconsCategoryTrailer, IconsCategoryGenerhvervelse, IconsCategorySaerligPlan } from '#components';

const queryClient = useQueryClient();
await queryClient.ensureQueryData(licenseIndexPageQueryOptions());
const { page, entrance, categories, products: productsBlock } = useLicenseIndexPage();

const productIds = computed(() => {
  const categoryIds = categories.value?.value.content ?? [];
  const listIds = productsBlock.value?.value.content?.map(p => p.product) ?? [];
  return [...new Set([...categoryIds, ...listIds])];
});

if (productIds.value.length) {
  await queryClient.ensureQueryData(productsQueryOptions({ ids: productIds.value }));
}
const { getById } = useProducts({ ids: productIds });

useSeo({ page });

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      { name: 'Forside', item: '/' },
      { name: 'Kørekort' },
    ],
  }),
]);

const illustrationMap: Record<string, Component> = {
  'bil': IconsCategoryBil,
  'trailer': IconsCategoryTrailer,
  'generhvervelse': IconsCategoryGenerhvervelse,
  'særlig-plan': IconsCategorySaerligPlan,
};

const resolvedCategories = computed(() =>
  (categories.value?.value.content ?? [])
    .map(id => getById(id))
    .filter(c => c?.product.meta.slug),
);
</script>

<template>
  <div>
    <!-- Entrance -->
    <section
      v-if="entrance"
      class="py-24 md:py-32"
    >
      <div class="max-w-3xl mx-auto px-6 text-center">
        <h1 class="text-4xl md:text-5xl font-bold">
          {{ entrance.value.heading }}
        </h1>
        <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
        <div
          class="mt-6 mx-auto text-[var(--color-charcoal)]/70 cms-prose"
          v-html="entrance.value.content"
        />
      </div>
    </section>

    <!-- Categories -->
    <section
      v-if="resolvedCategories.length"
      class="pb-24"
    >
      <div class="max-w-6xl mx-auto px-6">
        <h2 class="text-center text-3xl font-bold mb-8">
          {{ categories!.value.heading }}
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <NuxtLink
            v-for="cat in resolvedCategories"
            :key="cat.product.id"
            :to="{ name: `kørekort-${cat.product.meta.slug}` }"
            class="group block"
          >
            <div class="h-full bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 flex flex-col items-center text-center">
              <div class="w-28 h-28 mb-5 text-primary-600/80 group-hover:text-primary-600 transition-colors">
                <component
                  :is="illustrationMap[cat.product.meta.slug]"
                />
              </div>
              <h3 class="text-xl font-semibold text-[var(--color-charcoal)] group-hover:text-primary-600 transition-colors">
                {{ cat.heading?.value }}
              </h3>
              <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
              <div
                v-if="cat.shortDescription?.value"
                class="mt-3 text-sm text-[var(--color-charcoal)]/70 mx-auto cms-prose"
                v-html="cat.shortDescription.value"
              />
              <p class="mt-4 text-sm font-medium text-primary-600 group-hover:underline">
                Tilmeld &rarr;
              </p>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- Products list -->
    <section
      v-if="productsBlock?.value.content?.length"
      class="pb-24"
    >
      <div class="max-w-4xl mx-auto px-6 text-center">
        <h2 class="text-2xl font-bold">
          {{ productsBlock.value.heading }}
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
        <div
          v-if="productsBlock.value.description"
          class="mt-2 text-sm text-[var(--color-charcoal)]/70 mx-auto cms-prose"
          v-html="productsBlock.value.description"
        />

        <ul class="mt-8 divide-y divide-neutral-200">
          <li
            v-for="product in productsBlock.value.content"
            :key="product.product"
            class="flex items-center justify-between py-4"
          >
            <span class="font-medium">{{ getById(product.product)?.heading?.value }}</span>
            <!-- eslint-disable-next-line vue/no-v-html cms is safe -->
            <div
              class="text-sm text-[var(--color-charcoal)]/70 [&>p]:m-0"
              v-html="getById(product.product)?.price?.value"
            />
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>
